import React, { useEffect, useRef, useState, useCallback } from "react";
import { Play, ArrowClockwise, SpeakerHigh, SpeakerSimpleSlash } from "@phosphor-icons/react";

interface Pipe {
  x: number;
  topHeight: number;
  bottomY: number;
  passed: boolean;
}

interface Star {
  x: number;
  y: number;
  char: string;
  speed: number;
}

export const AsciiFlappyBird: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [gameState, setGameState] = useState<"READY" | "PLAYING" | "GAMEOVER">("READY");
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("flappy_best");
      return saved ? parseInt(saved, 10) || 0 : 0;
    }
    return 0;
  });
  const [isMuted, setIsMuted] = useState(false);

  // Audio Context Ref
  const audioCtxRef = useRef<AudioContext | null>(null);

  const getAudioContext = useCallback(() => {
    if (!audioCtxRef.current && typeof window !== "undefined") {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AudioCtx) {
        audioCtxRef.current = new AudioCtx();
      }
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playBeep = useCallback((freq: number, type: OscillatorType, duration: number, endFreq?: number) => {
    if (isMuted) return;
    try {
      const ctx = getAudioContext();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      if (endFreq) {
        osc.frequency.exponentialRampToValueAtTime(endFreq, ctx.currentTime + duration);
      }

      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch {
      // Audio autoplay policy fallback
    }
  }, [getAudioContext, isMuted]);

  // Game loop state refs to avoid closure lag in requestAnimationFrame
  const stateRef = useRef({
    state: "READY" as "READY" | "PLAYING" | "GAMEOVER",
    birdY: 140,
    birdVelocity: 0,
    birdRotation: 0,
    score: 0,
    bestScore: bestScore,
    pipes: [] as Pipe[],
    stars: [] as Star[],
    frameCount: 0,
    groundOffset: 0,
  });

  stateRef.current.bestScore = bestScore;

  // Initialize background stars / ASCII dust
  useEffect(() => {
    const stars: Star[] = [];
    const chars = [".", "*", "+", "·", "°", "~"];
    for (let i = 0; i < 24; i++) {
      stars.push({
        x: Math.random() * 640,
        y: Math.random() * 240,
        char: chars[Math.floor(Math.random() * chars.length)],
        speed: 0.2 + Math.random() * 0.5,
      });
    }
    stateRef.current.stars = stars;
  }, []);

  const flap = useCallback(() => {
    const s = stateRef.current;
    if (s.state === "READY") {
      s.state = "PLAYING";
      s.birdY = 140;
      s.birdVelocity = -5.8;
      s.score = 0;
      s.pipes = [];
      setScore(0);
      setGameState("PLAYING");
      playBeep(420, "triangle", 0.08, 620);
      return;
    }

    if (s.state === "PLAYING") {
      s.birdVelocity = -5.6;
      playBeep(440, "triangle", 0.07, 680);
      return;
    }

    if (s.state === "GAMEOVER") {
      s.state = "PLAYING";
      s.birdY = 140;
      s.birdVelocity = -5.8;
      s.score = 0;
      s.pipes = [];
      setScore(0);
      setGameState("PLAYING");
      playBeep(420, "triangle", 0.08, 620);
    }
  }, [playBeep]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" || e.code === "ArrowUp" || e.code === "KeyW") {
        // Prevent default window scrolling only if the game canvas is in view
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          if (rect.top < window.innerHeight && rect.bottom > 0) {
            e.preventDefault();
            flap();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [flap]);

  // Main Canvas Rendering & Physics Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;
    const CANVAS_WIDTH = 640;
    const CANVAS_HEIGHT = 360;
    const GROUND_HEIGHT = 44;
    const PLAYABLE_HEIGHT = CANVAS_HEIGHT - GROUND_HEIGHT;
    const GRAVITY = 0.32;
    const PIPE_GAP = 105;
    const PIPE_WIDTH = 48;
    const PIPE_SPEED = 2.4;

    const render = () => {
      const s = stateRef.current;
      s.frameCount++;

      // 1. Physics update
      if (s.state === "PLAYING") {
        s.birdVelocity += GRAVITY;
        s.birdY += s.birdVelocity;

        // Ground offset animation
        s.groundOffset = (s.groundOffset + PIPE_SPEED) % 24;

        // Spawn pipes every 105 frames (~1.7s)
        if (s.frameCount % 105 === 0) {
          const minHeight = 45;
          const maxHeight = PLAYABLE_HEIGHT - PIPE_GAP - 45;
          const topHeight = Math.floor(Math.random() * (maxHeight - minHeight + 1)) + minHeight;
          s.pipes.push({
            x: CANVAS_WIDTH + 10,
            topHeight,
            bottomY: topHeight + PIPE_GAP,
            passed: false,
          });
        }

        // Move pipes & check score/collision
        for (let i = s.pipes.length - 1; i >= 0; i--) {
          const pipe = s.pipes[i];
          pipe.x -= PIPE_SPEED;

          // Score check
          const birdX = 90;
          if (!pipe.passed && pipe.x + PIPE_WIDTH < birdX) {
            pipe.passed = true;
            s.score += 1;
            setScore(s.score);
            playBeep(880, "sine", 0.1, 1320);

            if (s.score > s.bestScore) {
              s.bestScore = s.score;
              setBestScore(s.score);
              localStorage.setItem("flappy_best", s.score.toString());
            }
          }

          // Collision check: bird dimensions approx 28px width, 18px height
          const birdBox = {
            left: birdX - 12,
            right: birdX + 14,
            top: s.birdY - 9,
            bottom: s.birdY + 9,
          };

          // Check if bird hits upper pipe or lower pipe
          if (birdBox.right > pipe.x && birdBox.left < pipe.x + PIPE_WIDTH) {
            if (birdBox.top < pipe.topHeight || birdBox.bottom > pipe.bottomY) {
              // Hit pipe!
              s.state = "GAMEOVER";
              setGameState("GAMEOVER");
              playBeep(180, "sawtooth", 0.22, 60);
            }
          }

          // Remove offscreen pipes
          if (pipe.x < -PIPE_WIDTH - 20) {
            s.pipes.splice(i, 1);
          }
        }

        // Check ground and ceiling collision
        if (s.birdY + 9 >= PLAYABLE_HEIGHT) {
          s.birdY = PLAYABLE_HEIGHT - 9;
          s.state = "GAMEOVER";
          setGameState("GAMEOVER");
          playBeep(180, "sawtooth", 0.22, 60);
        } else if (s.birdY - 9 <= 0) {
          s.birdY = 9;
          s.birdVelocity = 0;
        }

        // Update stars position
        for (const star of s.stars) {
          star.x -= star.speed;
          if (star.x < 0) {
            star.x = CANVAS_WIDTH;
            star.y = Math.random() * 240;
          }
        }
      } else if (s.state === "READY") {
        // Gentle bobbing animation when waiting
        s.birdY = 140 + Math.sin(s.frameCount * 0.08) * 8;
        s.groundOffset = (s.groundOffset + 1) % 24;
      }

      // 2. Drawing Scene
      // Clear terminal screen with dark charcoal background
      ctx.fillStyle = "#121110";
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw subtle terminal CRT scanline pattern
      ctx.fillStyle = "rgba(255, 255, 255, 0.015)";
      for (let y = 0; y < CANVAS_HEIGHT; y += 4) {
        ctx.fillRect(0, y, CANVAS_WIDTH, 1.5);
      }

      // Draw background ASCII stars
      ctx.fillStyle = "#4A4843";
      ctx.font = "12px 'JetBrains Mono', monospace";
      for (const star of s.stars) {
        ctx.fillText(star.char, star.x, star.y);
      }

      // Draw ASCII Pipes
      ctx.font = "bold 13px 'JetBrains Mono', monospace";
      for (const pipe of s.pipes) {
        // Upper pipe
        ctx.fillStyle = "#262522";
        ctx.fillRect(pipe.x, 0, PIPE_WIDTH, pipe.topHeight);
        ctx.strokeStyle = "#3E3C37";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(pipe.x, 0, PIPE_WIDTH, pipe.topHeight);

        // Pipe Lip / Collar (Top Pipe)
        ctx.fillStyle = "#2D2B27";
        ctx.fillRect(pipe.x - 3, pipe.topHeight - 14, PIPE_WIDTH + 6, 14);
        ctx.strokeRect(pipe.x - 3, pipe.topHeight - 14, PIPE_WIDTH + 6, 14);

        // ASCII text pattern inside pipe
        ctx.fillStyle = "#E5983A";
        ctx.fillText("[====]", pipe.x + 1, pipe.topHeight - 3);
        ctx.fillStyle = "#5C5952";
        for (let py = 16; py < pipe.topHeight - 16; py += 16) {
          ctx.fillText("|    |", pipe.x + 2, py);
        }

        // Lower pipe
        const bottomHeight = PLAYABLE_HEIGHT - pipe.bottomY;
        ctx.fillStyle = "#262522";
        ctx.fillRect(pipe.x, pipe.bottomY, PIPE_WIDTH, bottomHeight);
        ctx.strokeRect(pipe.x, pipe.bottomY, PIPE_WIDTH, bottomHeight);

        // Pipe Lip / Collar (Bottom Pipe)
        ctx.fillStyle = "#2D2B27";
        ctx.fillRect(pipe.x - 3, pipe.bottomY, PIPE_WIDTH + 6, 14);
        ctx.strokeRect(pipe.x - 3, pipe.bottomY, PIPE_WIDTH + 6, 14);

        // ASCII text pattern inside bottom pipe
        ctx.fillStyle = "#E5983A";
        ctx.fillText("[====]", pipe.x + 1, pipe.bottomY + 11);
        ctx.fillStyle = "#5C5952";
        for (let py = pipe.bottomY + 28; py < PLAYABLE_HEIGHT - 6; py += 16) {
          ctx.fillText("|    |", pipe.x + 2, py);
        }
      }

      // Draw Ground
      ctx.fillStyle = "#181716";
      ctx.fillRect(0, PLAYABLE_HEIGHT, CANVAS_WIDTH, GROUND_HEIGHT);
      ctx.strokeStyle = "#2E2C28";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, PLAYABLE_HEIGHT);
      ctx.lineTo(CANVAS_WIDTH, PLAYABLE_HEIGHT);
      ctx.stroke();

      // Moving ground ASCII diagonal slashes
      ctx.fillStyle = "#5A574F";
      ctx.font = "11px 'JetBrains Mono', monospace";
      let groundStr = "";
      for (let i = 0; i < 40; i++) {
        groundStr += "/// ";
      }
      ctx.fillText(groundStr, -s.groundOffset, PLAYABLE_HEIGHT + 18);
      ctx.fillText("================================================================", 0, PLAYABLE_HEIGHT + 32);

      // Draw ASCII Flappy Bird Character
      const birdX = 90;
      const birdY = s.birdY;

      ctx.save();
      ctx.translate(birdX, birdY);

      // Rotation based on velocity
      const angle = Math.min(Math.PI / 4, Math.max(-Math.PI / 5, s.birdVelocity * 0.08));
      ctx.rotate(angle);

      // Bird Wing Flap ASCII Sprite
      const isWingUp = s.birdVelocity < 0;
      const wingChar = isWingUp ? "/" : "\\";
      const spriteText = `(•)${wingChar}>`;

      // Draw warm ambient glow around the bird
      ctx.shadowColor = "#E5983A";
      ctx.shadowBlur = 10;

      // Draw bird body badge
      ctx.fillStyle = "#E5983A";
      ctx.font = "bold 15px 'JetBrains Mono', monospace";
      ctx.fillText(spriteText, -15, 5);

      ctx.restore();

      // Reset shadow blur
      ctx.shadowBlur = 0;

      // Draw In-Game Monospace Telemetry / Score
      ctx.fillStyle = "#F3F2EE";
      ctx.font = "bold 13px 'JetBrains Mono', monospace";
      ctx.fillText(`SCORE: ${String(s.score).padStart(2, "0")}`, CANVAS_WIDTH - 170, 28);
      ctx.fillStyle = "#8C8A84";
      ctx.fillText(`BEST: ${String(s.bestScore).padStart(2, "0")}`, CANVAS_WIDTH - 85, 28);

      // State Overlays
      if (s.state === "READY") {
        ctx.fillStyle = "rgba(18, 17, 16, 0.75)";
        ctx.fillRect(80, 80, CANVAS_WIDTH - 160, 160);
        ctx.strokeStyle = "#3E3C37";
        ctx.lineWidth = 1;
        ctx.strokeRect(80, 80, CANVAS_WIDTH - 160, 160);

        ctx.fillStyle = "#E5983A";
        ctx.font = "bold 16px 'JetBrains Mono', monospace";
        ctx.textAlign = "center";
        ctx.fillText("ASCII FLAPPY BIRD // V1.0", CANVAS_WIDTH / 2, 125);

        ctx.fillStyle = "#F3F2EE";
        ctx.font = "13px 'JetBrains Mono', monospace";
        const pulseAlpha = 0.5 + 0.5 * Math.sin(s.frameCount * 0.1);
        ctx.fillStyle = `rgba(243, 242, 238, ${pulseAlpha})`;
        ctx.fillText("[ PRESS SPACE OR TAP TO FLAP ]", CANVAS_WIDTH / 2, 175);

        ctx.fillStyle = "#8C8A84";
        ctx.font = "11px 'JetBrains Mono', monospace";
        ctx.fillText("DODGE THE PIPES · 60FPS TERMINAL PHYSICS", CANVAS_WIDTH / 2, 205);
        ctx.textAlign = "left";
      } else if (s.state === "GAMEOVER") {
        ctx.fillStyle = "rgba(18, 17, 16, 0.85)";
        ctx.fillRect(80, 75, CANVAS_WIDTH - 160, 175);
        ctx.strokeStyle = "#E5983A";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(80, 75, CANVAS_WIDTH - 160, 175);

        ctx.fillStyle = "#EF4444";
        ctx.font = "bold 17px 'JetBrains Mono', monospace";
        ctx.textAlign = "center";
        ctx.fillText("GAME OVER // CRASH DETECTED", CANVAS_WIDTH / 2, 115);

        ctx.fillStyle = "#F3F2EE";
        ctx.font = "14px 'JetBrains Mono', monospace";
        ctx.fillText(`FINAL SCORE: ${s.score}   BEST: ${s.bestScore}`, CANVAS_WIDTH / 2, 150);

        const pulseAlpha = 0.5 + 0.5 * Math.sin(s.frameCount * 0.1);
        ctx.fillStyle = `rgba(229, 152, 58, ${pulseAlpha})`;
        ctx.font = "bold 13px 'JetBrains Mono', monospace";
        ctx.fillText("[ PRESS SPACE OR TAP TO RETRY ]", CANVAS_WIDTH / 2, 195);

        ctx.fillStyle = "#8C8A84";
        ctx.font = "11px 'JetBrains Mono', monospace";
        ctx.fillText("Score stored in local profile memory", CANVAS_WIDTH / 2, 222);
        ctx.textAlign = "left";
      }

      animationFrameId = requestAnimationFrame(render);
    };

    animationFrameId = requestAnimationFrame(render);
    return () => cancelAnimationFrame(animationFrameId);
  }, [playBeep]);

  return (
    <div className="flex flex-col h-full select-none">
      {/* Game Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#171615] border-b border-[#242320] text-xs font-mono">
        <div className="flex items-center gap-2">
          <span className="text-[#E5983A] font-bold">ascii-flappy.sh</span>
          <span className="text-[#66645E]">--pid 8192</span>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-1 text-[#8C8A84] hover:text-[#E5983A] transition cursor-pointer"
            title={isMuted ? "Unmute sound" : "Mute sound"}
          >
            {isMuted ? <SpeakerSimpleSlash size={15} /> : <SpeakerHigh size={15} />}
          </button>

          <button
            onClick={flap}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-[#22211F] hover:bg-[#2C2A26] border border-[#2F2D29] text-[11px] text-[#DCD8CE] hover:text-[#E5983A] transition cursor-pointer"
          >
            {gameState === "GAMEOVER" ? (
              <>
                <ArrowClockwise size={12} weight="bold" />
                <span>Retry</span>
              </>
            ) : gameState === "READY" ? (
              <>
                <Play size={12} weight="fill" />
                <span>Start</span>
              </>
            ) : (
              <span>Flap!</span>
            )}
          </button>
        </div>
      </div>

      {/* Interactive Monospace Canvas */}
      <div
        className="relative w-full aspect-[16/9] sm:aspect-[2/1] bg-[#121110] cursor-pointer overflow-hidden"
        onClick={flap}
      >
        <canvas
          ref={canvasRef}
          width={640}
          height={360}
          className="w-full h-full block object-contain"
        />
      </div>

      {/* Game Footer / Controls Helper */}
      <div className="px-4 py-2 bg-[#141413] border-t border-[#242320] flex items-center justify-between text-[11px] font-mono text-[#787670]">
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 rounded bg-[#201F1D] border border-[#2D2B27] text-[#DCD8CE]">SPACE</span>
          <span className="hidden sm:inline">or</span>
          <span className="hidden sm:inline px-1.5 py-0.5 rounded bg-[#201F1D] border border-[#2D2B27] text-[#DCD8CE]">UP ARROW</span>
          <span>to flap wing</span>
        </div>

        <div className="text-[#8C8A84]">
          <span>Tap anywhere on mobile</span>
        </div>
      </div>
    </div>
  );
};
